/**
 * Marketing → Broadcasts sub-page. Compose HTML emails, target a list OR a
 * hand-picked subset of contacts, preview to your own inbox, and either
 * send-now or schedule for a future ISO datetime. Stats update live.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Send, Clock, Copy, Eye, Trash2, CheckCircle2, XCircle, PauseCircle, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

function statusBadge(status) {
  const map = {
    draft: { cls: "bg-muted text-muted-foreground", icon: <PauseCircle className="w-3 h-3" /> },
    scheduled: { cls: "bg-blue-500/15 text-blue-700", icon: <Clock className="w-3 h-3" /> },
    sending: { cls: "bg-yellow-500/15 text-yellow-800", icon: <Send className="w-3 h-3" /> },
    sent: { cls: "bg-primary/10 text-primary", icon: <CheckCircle2 className="w-3 h-3" /> },
    cancelled: { cls: "bg-destructive/15 text-destructive", icon: <XCircle className="w-3 h-3" /> },
  };
  const s = map[status] || map.draft;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] overline px-2 py-0.5 rounded-full ${s.cls}`}>
      {s.icon} {status}
    </span>
  );
}

export default function MarketingBroadcasts() {
  const [rows, setRows] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/marketing/broadcasts");
    setRows(data);
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 15_000); return () => clearInterval(iv); }, [load]);

  return (
    <div className="space-y-4" data-testid="mkt-broadcasts-page">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-2xl font-light">Broadcasts</h3>
          <p className="text-xs text-muted-foreground">Send one-off campaigns or schedule them for later.</p>
        </div>
        <Button onClick={() => setOpenNew(true)} data-testid="mkt-new-broadcast-btn">
          <Plus className="w-4 h-4 mr-2" /> New broadcast
        </Button>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 border-b border-border bg-muted/40 text-[11px] overline">
          <div className="col-span-5">Subject</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">When</div>
          <div className="col-span-2 text-right">Stats</div>
          <div className="col-span-1"></div>
        </div>
        {rows.length === 0 && <div className="p-10 text-center text-sm text-muted-foreground">No broadcasts yet.</div>}
        {rows.map((b) => (
          <button
            key={b.id}
            onClick={() => setSelected(b)}
            className="w-full text-left grid grid-cols-12 px-4 py-3 border-b border-border last:border-0 items-center hover:bg-muted/30 transition-colors"
            data-testid={`mkt-broadcast-${b.id}`}
          >
            <div className="col-span-5 min-w-0">
              <div className="font-medium truncate">{b.subject}</div>
              <div className="text-[11px] text-muted-foreground">created {new Date(b.created_at).toLocaleString()}</div>
            </div>
            <div className="col-span-2">{statusBadge(b.status)}</div>
            <div className="col-span-2 text-xs">
              {b.status === "sent" && b.sent_at && <span>Sent {new Date(b.sent_at).toLocaleString()}</span>}
              {b.status === "scheduled" && b.scheduled_for && <span className="text-blue-700">{new Date(b.scheduled_for).toLocaleString()}</span>}
              {b.status === "draft" && <span className="text-muted-foreground">Not scheduled</span>}
            </div>
            <div className="col-span-2 text-right text-xs">
              {b.stats?.sent > 0 && <span className="text-primary">{b.stats.sent} sent</span>}
              {b.stats?.failed > 0 && <span className="text-destructive ml-2">{b.stats.failed} fail</span>}
            </div>
            <div className="col-span-1"></div>
          </button>
        ))}
      </div>

      <BroadcastComposer open={openNew} onOpenChange={setOpenNew} onCreated={() => { setOpenNew(false); load(); }} />
      <BroadcastDrawer broadcast={selected} onClose={() => setSelected(null)} onChanged={load} />
    </div>
  );
}

// -------------------------------------------------------------------------
//   Composer  (used for both new broadcast + edit existing)
// -------------------------------------------------------------------------
function BroadcastComposer({ open, onOpenChange, onCreated, initial = null }) {
  const [lists, setLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({
    subject: "",
    body_html: "",
    list_id: "",
    contact_ids: [],
    scheduled_for: "",
    template_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [targetMode, setTargetMode] = useState("list");     // list | pick

  useEffect(() => {
    if (!open) return;
    api.get("/marketing/lists").then((r) => setLists(r.data || [])).catch(() => setLists([]));
    api.get("/marketing/templates").then((r) => setTemplates(r.data || [])).catch(() => setTemplates([]));
    if (initial) {
      setForm({
        subject: initial.subject || "",
        body_html: initial.body_html || "",
        list_id: initial.list_id || "",
        contact_ids: initial.contact_ids || [],
        scheduled_for: initial.scheduled_for ? initial.scheduled_for.slice(0, 16) : "",
        template_id: initial.template_id || "",
      });
      setTargetMode(initial.contact_ids?.length ? "pick" : "list");
    } else {
      setForm({ subject: "", body_html: "", list_id: "", contact_ids: [], scheduled_for: "", template_id: "" });
      setTargetMode("list");
    }
  }, [open, initial]);

  // Load contacts for the pick-mode search
  useEffect(() => {
    if (targetMode !== "pick" || !open) return;
    api.get("/marketing/contacts", { params: q ? { q } : {} })
      .then((r) => setContacts(r.data || []))
      .catch(() => setContacts([]));
  }, [targetMode, q, open]);

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setForm((f) => ({
      ...f,
      template_id: tid,
      subject: f.subject || t.subject,
      body_html: f.body_html || t.body_html,
    }));
    toast.success(`Applied template "${t.name}"`);
  };

  const submit = async (mode) => {
    setErr(null); setSaving(true);
    try {
      const payload = {
        subject: form.subject,
        body_html: form.body_html,
        list_id: targetMode === "list" ? form.list_id || null : null,
        contact_ids: targetMode === "pick" ? form.contact_ids : [],
        scheduled_for: mode === "schedule" ? new Date(form.scheduled_for).toISOString() : null,
        template_id: form.template_id || null,
      };
      const create = initial ? await api.put(`/marketing/broadcasts/${initial.id}`, payload) : await api.post("/marketing/broadcasts", payload);
      if (mode === "sendnow") {
        await api.post(`/marketing/broadcasts/${create.data.id}/send-now`);
        toast.success("Broadcast sent");
      } else if (mode === "schedule") {
        toast.success(`Scheduled for ${new Date(form.scheduled_for).toLocaleString()}`);
      } else {
        toast.success("Saved as draft");
      }
      onCreated?.();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const pickContact = (id) => {
    setForm((f) => {
      const s = new Set(f.contact_ids);
      if (s.has(id)) s.delete(id); else s.add(id);
      return { ...f, contact_ids: Array.from(s) };
    });
  };
  const pickAll = () => setForm((f) => ({ ...f, contact_ids: contacts.map((c) => c.id) }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-light text-2xl">{initial ? "Edit broadcast" : "New broadcast"}</DialogTitle>
          <DialogDescription>Compose, target and send — or schedule for later.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template shortcut */}
          {templates.length > 0 && (
            <div>
              <Label className="text-xs">Start from template (optional)</Label>
              <Select value={form.template_id || "none"} onValueChange={(v) => v !== "none" && applyTemplate(v)}>
                <SelectTrigger className="mt-1.5" data-testid="mkt-broadcast-template"><SelectValue placeholder="Blank" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Blank</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs">Subject</Label>
            <Input required data-testid="mkt-broadcast-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5" />
          </div>

          <div>
            <Label className="text-xs">HTML body</Label>
            <Textarea rows={10} required data-testid="mkt-broadcast-body" value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} className="mt-1.5 font-mono text-xs" placeholder="<p>Hi {{name}},</p><p>...</p>" />
            <p className="mt-1 text-[11px] text-muted-foreground">Inline CSS only. An unsubscribe link is appended automatically.</p>
          </div>

          <Tabs value={targetMode} onValueChange={setTargetMode}>
            <TabsList>
              <TabsTrigger value="list" data-testid="mkt-target-list">Send to a list</TabsTrigger>
              <TabsTrigger value="pick" data-testid="mkt-target-pick">Pick individual contacts</TabsTrigger>
            </TabsList>
            <TabsContent value="list" className="pt-3">
              <Select value={form.list_id || "none"} onValueChange={(v) => setForm({ ...form, list_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="mkt-broadcast-list"><SelectValue placeholder="Choose a list" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none —</SelectItem>
                  {lists.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name} {l.contact_count != null ? `(${l.contact_count})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </TabsContent>
            <TabsContent value="pick" className="pt-3 space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input placeholder="Search contacts…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
              </div>
              <div className="flex items-center justify-between text-xs">
                <span>{form.contact_ids.length} selected</span>
                <Button variant="ghost" size="sm" onClick={pickAll}>Select all shown</Button>
              </div>
              <div className="max-h-[220px] overflow-y-auto border border-border rounded-md">
                {contacts.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 border-b border-border last:border-0 text-sm hover:bg-muted/40">
                    <input type="checkbox" checked={form.contact_ids.includes(c.id)} onChange={() => pickContact(c.id)} />
                    <span className="font-mono text-xs">{c.email}</span>
                    <span className="text-muted-foreground truncate">{c.name}</span>
                  </label>
                ))}
                {contacts.length === 0 && <div className="p-4 text-xs text-muted-foreground">No contacts.</div>}
              </div>
            </TabsContent>
          </Tabs>

          <div>
            <Label className="text-xs">Schedule for (optional)</Label>
            <Input
              type="datetime-local"
              value={form.scheduled_for}
              onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })}
              className="mt-1.5"
              data-testid="mkt-broadcast-schedule"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">Leave blank to send now or save as draft.</p>
          </div>

          {err && <div className="text-sm text-destructive">{err}</div>}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => submit("draft")} disabled={saving} data-testid="mkt-broadcast-save-draft">Save draft</Button>
          {form.scheduled_for && (
            <Button variant="outline" onClick={() => submit("schedule")} disabled={saving} data-testid="mkt-broadcast-schedule-btn">
              <Clock className="w-4 h-4 mr-2" /> Schedule
            </Button>
          )}
          <Button onClick={() => submit("sendnow")} disabled={saving} data-testid="mkt-broadcast-send-now">
            <Send className="w-4 h-4 mr-2" /> Send now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------------------------------------------------------------
//   Drawer  (view + preview + delete)
// -------------------------------------------------------------------------
function BroadcastDrawer({ broadcast, onClose, onChanged }) {
  const [previewEmail, setPreviewEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [full, setFull] = useState(null);

  // The list endpoint strips body_html for size; refetch the full doc on open
  // so we can render the preview.
  useEffect(() => {
    if (!broadcast) { setFull(null); return; }
    api.get(`/marketing/broadcasts/${broadcast.id}`)
      .then((r) => setFull(r.data))
      .catch(() => setFull(broadcast));
  }, [broadcast]);

  if (!broadcast) return null;
  const b = full || broadcast;

  const preview = async () => {
    if (!previewEmail) return toast.error("Enter an email to send preview to");
    setBusy(true);
    try {
      await api.post(`/marketing/broadcasts/${broadcast.id}/preview?to_email=${encodeURIComponent(previewEmail)}`);
      toast.success(`Preview sent to ${previewEmail}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const sendNow = async () => {
    if (!window.confirm("Send this broadcast now?")) return;
    setBusy(true);
    try {
      await api.post(`/marketing/broadcasts/${broadcast.id}/send-now`);
      toast.success("Sent");
      onChanged?.(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    await api.put(`/marketing/broadcasts/${broadcast.id}`, { status: "cancelled", scheduled_for: null });
    toast.success("Cancelled");
    onChanged?.(); onClose();
  };
  const del = async () => {
    if (!window.confirm("Delete this broadcast?")) return;
    await api.delete(`/marketing/broadcasts/${broadcast.id}`);
    onChanged?.(); onClose();
  };

  return (
    <Dialog open={!!broadcast} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-light text-2xl">{b.subject}</DialogTitle>
          <DialogDescription className="flex items-center gap-2 mt-1">{statusBadge(b.status)}
            <span className="text-xs text-muted-foreground">· created {new Date(b.created_at).toLocaleString()}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Preview HTML */}
        {b.body_html && (
          <div className="border border-border rounded-md overflow-hidden bg-white">
            <div className="px-3 py-1.5 border-b border-border bg-muted/40 text-[10px] overline text-muted-foreground">Rendered preview</div>
            <div className="p-4 max-h-[300px] overflow-y-auto" data-testid="mkt-broadcast-preview" dangerouslySetInnerHTML={{ __html: b.body_html }} />
          </div>
        )}

        {b.status === "sent" && (
          <div className="grid grid-cols-3 gap-3">
            <StatMini label="Sent" value={b.stats?.sent || 0} tone="teal" />
            <StatMini label="Failed" value={b.stats?.failed || 0} tone="rose" />
            <StatMini label="Skipped" value={b.stats?.skipped || 0} tone="muted" />
          </div>
        )}

        {b.status !== "sent" && (
          <>
            <div className="flex flex-col sm:flex-row gap-2 items-center">
              <Input type="email" placeholder="you@yourdomain.com" value={previewEmail} onChange={(e) => setPreviewEmail(e.target.value)} data-testid="mkt-preview-email" />
              <Button variant="outline" size="sm" onClick={preview} disabled={busy} data-testid="mkt-preview-btn">
                <Eye className="w-4 h-4 mr-2" /> Send preview
              </Button>
            </div>
          </>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          {b.status !== "sent" && (
            <Button variant="outline" onClick={() => setOpenEdit(true)} data-testid="mkt-broadcast-edit">Edit</Button>
          )}
          {b.status === "scheduled" && (
            <Button variant="ghost" className="text-destructive" onClick={cancel}>Cancel schedule</Button>
          )}
          {b.status !== "sent" && (
            <Button variant="ghost" className="text-destructive" onClick={del}><Trash2 className="w-4 h-4 mr-2" /> Delete</Button>
          )}
          {b.status !== "sent" && (
            <Button onClick={sendNow} disabled={busy} data-testid="mkt-drawer-send-now">
              <Send className="w-4 h-4 mr-2" /> Send now
            </Button>
          )}
        </DialogFooter>

        <BroadcastComposer open={openEdit} onOpenChange={setOpenEdit} initial={b} onCreated={() => { setOpenEdit(false); onChanged?.(); onClose(); }} />
      </DialogContent>
    </Dialog>
  );
}

function StatMini({ label, value, tone }) {
  const map = { teal: "text-primary", rose: "text-destructive", muted: "text-muted-foreground" };
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-[10px] overline text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display text-2xl ${map[tone] || ""}`}>{value}</div>
    </div>
  );
}
